import { useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import moment from 'moment';
import { Accordion, Alert, Button, Card, Col, Form, Modal, Row, Spinner, Table } from 'react-bootstrap';
import {
  AdminGrantSongpopLegacyPackMutation,
  AdminGrantSongpopLegacyPackMutationVariables,
  AdminRevokeSongpopLegacyPackMutation,
  AdminRevokeSongpopLegacyPackMutationVariables,
  AdminSongpopLegacyPackStatusFragmentFragment,
  AdminSongpopLegacyPackStatusQuery,
  AdminSongpopLegacyPackStatusQueryVariables,
  GetSongpopLegacyPackConfigQuery,
  GetSongpopLegacyPackDropsQuery,
  SongpopLegacyPackUserState,
} from '../../../../../../__gqltypes__';
import {
  ADMIN_GRANT_SONGPOP_LEGACY_PACK,
  ADMIN_REVOKE_SONGPOP_LEGACY_PACK,
  ADMIN_SONGPOP_LEGACY_PACK_STATUS,
} from '../../../graphql';
import { formatReward } from '../../../../liveops/components/formatReward';
import {
  SONGPOP_LEGACY_PACK_CONFIG_QUERY,
  SONGPOP_LEGACY_PACK_DROPS_QUERY,
} from '../../../../liveops/screens/SongpopLegacyPack/graphql';
import { dropStatusBadge } from '../../../../liveops/screens/SongpopLegacyPack/utils';
import { LegacyPackEditForm } from './LegacyPackEditForm';

/** Active = the flag the game reads for the perks is on; every other state is not active. */
export const isLegacyPackActive = (state: SongpopLegacyPackUserState) =>
  state === SongpopLegacyPackUserState.OWNED || state === SongpopLegacyPackUserState.FLAG_WITHOUT_ROW;

const formatDate = (date?: string | null) => (date ? moment(date).format('YYYY-MM-DD HH:mm') : '-');

/**
 * Songpop Legacy Pack of one player. The status is Active when the perks flag is on, Not active
 * otherwise (no repair; the edit form below shows the pass row itself). Support grant/revoke work on every environment,
 * production included, and always ask for a reason, which is stored on the pass.
 */
const LegacyPackDisplay = ({ userId }: { userId: string }) => {
  const [expanded, setExpanded] = useState(false);
  const [supportAction, setSupportAction] = useState<null | 'grant' | 'revoke'>(null);
  const [reason, setReason] = useState('');
  const [grantDropId, setGrantDropId] = useState('');
  const [grantNotice, setGrantNotice] = useState<string | null>(null);
  const [latest, setLatest] = useState<AdminSongpopLegacyPackStatusFragmentFragment | null>(null);

  const {
    data,
    loading,
    error: loadError,
  } = useQuery<AdminSongpopLegacyPackStatusQuery, AdminSongpopLegacyPackStatusQueryVariables>(
    ADMIN_SONGPOP_LEGACY_PACK_STATUS,
    { variables: { userId }, fetchPolicy: 'no-cache', skip: !expanded }
  );

  const onStatus = (status?: AdminSongpopLegacyPackStatusFragmentFragment | null) => status && setLatest(status);
  const [grant, { loading: granting, error: grantError }] = useMutation<
    AdminGrantSongpopLegacyPackMutation,
    AdminGrantSongpopLegacyPackMutationVariables
  >(ADMIN_GRANT_SONGPOP_LEGACY_PACK, {
    onCompleted: (result) => {
      onStatus(result.adminGrantSongpopLegacyPack.status);
      setGrantNotice(
        result.adminGrantSongpopLegacyPack.instantRewardsGranted
          ? 'Pass granted with the exclusive rewards (and the daily chest, when it is on).'
          : 'The player already owned the pass: nothing was granted again.'
      );
    },
  });
  // The exclusive rewards and the daily chest are global (Songpop Maestro admin, member benefits).
  const { data: configData } = useQuery<GetSongpopLegacyPackConfigQuery>(SONGPOP_LEGACY_PACK_CONFIG_QUERY, {
    fetchPolicy: 'cache-and-network',
    skip: !expanded,
  });
  const memberConfig = configData?.liveOps.songpopLegacyPackConfig;
  // Every sale config, ended ones included: a dispute usually comes after the sale is over.
  const { data: dropsData } = useQuery<GetSongpopLegacyPackDropsQuery>(SONGPOP_LEGACY_PACK_DROPS_QUERY, {
    fetchPolicy: 'no-cache',
    skip: !expanded,
  });
  const drops = [...(dropsData?.liveOps.songpopLegacyPackDrops ?? [])].sort(
    (a, b) => new Date(b.startDate).getTime() - new Date(a.startDate).getTime()
  );
  const grantDrop = drops.find((drop) => drop.id === grantDropId);
  const [revoke, { loading: revoking, error: revokeError }] = useMutation<
    AdminRevokeSongpopLegacyPackMutation,
    AdminRevokeSongpopLegacyPackMutationVariables
  >(ADMIN_REVOKE_SONGPOP_LEGACY_PACK, { onCompleted: (result) => onStatus(result.adminRevokeSongpopLegacyPack) });

  const status = latest ?? data?.liveOps.adminSongpopLegacyPackStatus ?? null;
  const busy = granting || revoking;
  const error = loadError ?? grantError ?? revokeError;
  const ignore = () => {
    // surfaced through `error`
  };

  const runSupportAction = () => {
    setGrantNotice(null);
    const run =
      supportAction === 'grant'
        ? grant({ variables: { input: { userId, reason, dropId: grantDropId } } })
        : revoke({ variables: { input: { userId, reason } } });
    run.then(() => setReason('')).catch(ignore);
    setSupportAction(null);
  };

  return (
    <Card className="mb-2 shadow-sm border-0">
      <Card.Body className="py-2">
        <Accordion>
          <Accordion.Toggle as={Card.Title} variant="link" eventKey="0" onClick={() => setExpanded(!expanded)}>
            <div style={{ cursor: 'pointer', display: 'flex', justifyContent: 'space-between' }}>
              Songpop Maestro
              <Button variant="link">
                {!expanded && <i className="fas fa-chevron-down" />}
                {expanded && <i className="fas fa-chevron-up" />}
              </Button>
            </div>
          </Accordion.Toggle>
          <Accordion.Collapse eventKey="0">
            <>
              {loading && <Spinner animation="border" />}
              {error && <Alert variant="danger">{error.message}</Alert>}
              {status && (
                <>
                  <Alert variant={isLegacyPackActive(status.state) ? 'success' : 'secondary'}>
                    {isLegacyPackActive(status.state) ? 'Active' : 'Not active'}
                  </Alert>
                  <LegacyPackEditForm status={status} drops={drops} onSaved={setLatest} disabled={busy} />
                </>
              )}

              <h6>Support (every environment, production included)</h6>
              {grantNotice && <Alert variant="info">{grantNotice}</Alert>}
              <Row className="mb-2">
                <Col md={8}>
                  <Form.Control as="select" value={grantDropId} onChange={(e) => setGrantDropId(e.target.value)}>
                    <option value="">Sale config the player bought through (required to grant)</option>
                    {drops.map((drop) => (
                      <option key={drop.id} value={drop.id}>
                        {drop.name} - {drop.skuId} - {dropStatusBadge(drop).label} ({formatDate(drop.startDate)})
                      </option>
                    ))}
                  </Form.Control>
                  {memberConfig && (
                    <small className="text-muted">
                      Gives: exclusive rewards{' '}
                      {memberConfig.instantRewards.length > 0
                        ? memberConfig.instantRewards.map((reward) => formatReward(reward)).join(', ')
                        : 'none'}
                      ; daily chest{' '}
                      {memberConfig.chestEnabled && memberConfig.chestPool?.contents.length ? 'on' : 'off'}.
                    </small>
                  )}
                </Col>
              </Row>
              <Row className="mb-2">
                <Col md={8}>
                  <Form.Control
                    placeholder="Reason: ticket id, dispute... (required, stored on the pass)"
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                  />
                </Col>
                <Col md="auto">
                  <Button
                    variant="success"
                    className="mr-2"
                    disabled={busy || reason.trim().length < 5 || !grantDropId}
                    title={grantDropId ? '' : 'Pick the sale config first'}
                    onClick={() => setSupportAction('grant')}
                  >
                    Grant pass
                  </Button>
                  <Button
                    variant="danger"
                    disabled={busy || reason.trim().length < 5}
                    onClick={() => setSupportAction('revoke')}
                  >
                    Revoke pass
                  </Button>
                </Col>
              </Row>
              <small className="text-muted">
                A support grant gives everything a purchase gives: a seat counted on the chosen sale config, the
                exclusive rewards and the daily chest for life (no loyalty points). An owner gets nothing again.
              </small>
            </>
          </Accordion.Collapse>
        </Accordion>
      </Card.Body>

      <Modal show={supportAction !== null} onHide={() => setSupportAction(null)}>
        <Modal.Header closeButton>
          <Modal.Title>{supportAction === 'grant' ? 'Grant the pass?' : 'Revoke the pass?'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          This changes a real entitlement (beta and prod share the database). Reason: <em>{reason}</em>
          {supportAction === 'grant' && grantDrop && (
            <div className="mt-2">
              Sale config: <strong>{grantDrop.name}</strong> - the exclusive rewards are given now.
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setSupportAction(null)}>
            Cancel
          </Button>
          <Button variant={supportAction === 'grant' ? 'success' : 'danger'} onClick={runSupportAction}>
            {supportAction === 'grant' ? 'Grant' : 'Revoke'}
          </Button>
        </Modal.Footer>
      </Modal>
    </Card>
  );
};

export default LegacyPackDisplay;
